import { Component, Input, OnInit, SimpleChanges } from '@angular/core';
import { Filters } from 'src/app/global/filter-model';
import { EChartsOption } from 'echarts';
import { MixpanelService } from 'src/app/core/services/mixpanel.service';
import { SolutionPartService } from '../../service/solution-part.service';
import { FiltersService } from 'src/app/core/services/filters.service';

@Component({
    selector: 'solution-group-graph',
    templateUrl: './solution-group-graph.component.html',
    styleUrls: ['./solution-group-graph.component.scss'],
    standalone: false
})
export class SolutionGroupGraphComponent implements OnInit {

  filters: Filters | undefined;
  chartOptions: EChartsOption | any;
  isExpand: boolean = false;
  @Input() solutionGroupNames: string[] | undefined;
  @Input() chartValues: number[] | undefined;
  @Input() complianceLocationNumbers: number[] | undefined;
  @Input() totalLocationNumbers: number[] | undefined;

  @Input() opportunityValues: number[] | undefined;
  @Input() id: string = '';
  @Input() graphName: string = '';
  @Input() currencySymbol: string = '';

  displayLoader: boolean = true;
  isDataAvailable: boolean = true;
  displayCaretIcon: boolean = true;
  toggleState$ = this.solutionPartService.getToggleObservable();

  constructor(private mixpanelService: MixpanelService,
    private solutionPartService: SolutionPartService,
    private filtersService: FiltersService) {

  }

  ngOnChanges(changes: SimpleChanges): void {
    //Show data or error block based on array length
    if (changes['solutionGroupNames'].currentValue.length > 0) {
      this.displayCaretIcon = Boolean(this.solutionGroupNames?.length && this.solutionGroupNames.length > 5);
      this.isDataAvailable = true;
    } else {
      this.isDataAvailable = false;
    }

    this.initializeChartOptions();
  }

  ngOnInit(): void {
    this.toggleState$.subscribe(value => {
      this.isExpand = value;
      this.updateChartOptions();
    });
  }

  toggleClass() {
    this.solutionPartService.toggleAllGraphs();
    let eventType = this.isExpand ? "Expand Icon Clicked" : "Collapse Icon Clicked";
    // Push metrics to mixpanel
    this.mixpanelService.track(eventType, {
      "countriesSelected": this.filters?.countries,
      "customerNumberSelected": this.filters?.customers?.map(customer => customer.accountNumber),
      "H5s": this.filters?.topLevelHierarchies?.map(topLevelHierarchy => topLevelHierarchy.h5Number),
      "H7s": this.filters?.h7Agreements?.map(h7 => h7.number),
      "periodSelected": this.filters?.period,
      "Custom Date Range": this.filters?.dateRange,
      "regionSelected": this.filters?.regions,
      "templateSelected": this.filters?.presentationType ? this.filters.presentationType : 'All',
      "templateTranslationSelected": this.filters?.translation ? this.filters.translation : 'English',
      "Page Name": "Solution Participation",
      "Chart Name": this.graphName
    });
  }

  private initializeChartOptions(): void {
    this.chartOptions = {
      tooltip: {
        trigger: 'item',
        padding: 0,
        axisPointer: {
          type: 'none'
        }
      },
      xAxis: this.getXAxisConfiguration(this.graphName),
      yAxis: this.getYAxisConfiguration(this.solutionGroupNames?.slice(0, 5)),
      series: this.getSeriesConfiguration(this.graphName),
      grid: { top: 20, left: 120, right: 20, bottom: 10 }
    };
  }

  public updateChartOptions(): void {
    const yAxisData = this.isExpand ? this.solutionGroupNames : this.solutionGroupNames?.slice(0, 5);

    this.chartOptions = {
      ...this.chartOptions, // Retain other chart option properties
      yAxis: {
        ...this.chartOptions.yAxis,
        data: yAxisData,
      },
      series: this.getSeriesConfiguration(this.graphName)
    }
  }

  private getXAxisConfiguration(graphName: string) {
    if (graphName === "Top Solution Groups") {
      return {
        type: 'value',
        interval: 25,
        position: 'top',
        min: 0,
        max: 100,
        axisLabel: {
          ...this.getLabelTextStyle(),
        }
      }
    } else {
      return {
        type: 'value',
        position: 'top',
        axisLabel: {
          ...this.getLabelTextStyle(),
          formatter: (value: number) => {
            if (value < 1000000) {
              return value / 1000 + 'K'; // Show in thousands
            } else {
              return value / 1000000 + 'M'; // Show in millions
            }
          }
        }
      }
    }

  }

  private createSeriesData(salesData: number[], oppData: number[]): { value: number; total: number }[] {
    const totals = salesData.map((value, index) => value + oppData[index]);
    return salesData.map((value, index) => ({ value, total: totals[index] }));
  }

  private formatDollarValue(value: number) {
    if (value < 1000) {
      return Math.round((value) * 10) / 10;
    } else if (value > 1000 && value < 1000000) {
      return Math.round((value / 1000) * 10) / 10 + 'K'; // Show in thousands
    } else {
      return Math.round((value / 1000000) * 10) / 10 + 'M'; // Show in millions
    }
  }

  private getSeriesConfiguration(graphName: string) {
    const chartData = this.isExpand ? this.chartValues : this.chartValues?.slice(0, 5);
    const opportunitiesData = this.isExpand ? this.opportunityValues : this.opportunityValues?.slice(0, 5); // Used for top sales chart
    if (graphName === "Top Solution Groups") {
      const locationComplianceData = this.isExpand ? this.complianceLocationNumbers : this.complianceLocationNumbers?.slice(0, 5);
      const locationTotalData = this.isExpand ? this.totalLocationNumbers : this.totalLocationNumbers?.slice(0, 5);

      return [{
        data: chartData,
        type: 'bar',
        barWidth: 20,
        label: { show: false },
        itemStyle: { color: '#39B54A' },
        tooltip: {
          formatter: (params: any) => {
            let index = params.dataIndex; // Get the index of the current data point
            let percentUsage;
            let compliantLocations;
            let totalLocations;

            if (chartData) {
              percentUsage = chartData[index] + "%";
            }
            if (locationComplianceData) {
              compliantLocations = locationComplianceData[index];
            }
            if (locationTotalData) {
              totalLocations = locationTotalData[index];
            }
            return `<div class="tooltip-container" style="border-radius: 2px; border: 0.5px solid #39B54A; background: #ffffff; box-shadow: 0px 3px 10px 0px rgba(0, 107, 211, 0.15); padding: 8px;">
                    <div class="graph-legend"  style="font-size: 12px; font-weight: 400; font-family:Roboto; color: #313233; padding-bottom: 8px;">
                      <span class="circle" style="width: 12px; height: 12px; background-color: #39B54A; border-radius: 50%; display: inline-block; margin-right: 8px;"></span>Usage Locations
                    </div>
                    <div style="display:flex; align-items:center; gap:8px; ">
                      <div class="total-sales" style="font-size:28px; font-weight: 300; font-family:Roboto; color:#313233;">${percentUsage}</div>
                      <div style="font-size:12px; color:#616365; font-weight=400; font-family:Roboto; line-height: 16px;">
                        <br />
                      of total locations
                      </div>
                    </div>
                    <div style="font-size:12px; padding-top:8px">
                      <div style="margin-bottom:-6px"> <span class="compliant-locations" style="font-weight: 500; font-family:Roboto; color:#39B54A;">${compliantLocations}</span> usage locations across </div>
                      <span class="total-locations" style="font-weight: 500; font-family:Roboto; color:#313233;">${totalLocations}</span> total locations
                    </div>
                  </div>
                  `;
          }
        }
      }]
    } else {
      //Create an array of objects with values and total to be shown on the tooltip
      const salesSeriesData = this.createSeriesData(chartData!, opportunitiesData!);
      const oportunitiesSeriesData = this.createSeriesData(opportunitiesData!, chartData!);

      {
        return [{
          data: salesSeriesData,
          type: 'bar',
          name: 'sales',
          barWidth: 20,
          stack: 'stack1',
          label: { show: false },
          itemStyle: { color: '#1AA5E0' },
          tooltip: {
            formatter: (params: any) => {
              let salesValue = this.formatDollarValue(params.value);
              let totalValue = this.formatDollarValue(params.data.total);
              return `<div class="tooltip-container" style="border-radius: 2px; border: 0.5px solid #1AA5E0; background: #ffffff; box-shadow: 0px 3px 10px 0px rgba(0, 107, 211, 0.15); padding: 8px;">
                    <div class="graph-legend"  style="font-size: 12px; font-weight: 400; font-family:Roboto; color: #313233; padding-bottom: 8px;">
                      <span class="circle" style="width: 12px; height: 12px; background-color: #1AA5E0; border-radius: 50%; display: inline-block; margin-right: 8px;"></span>Field Sales
                    </div>
                    <div style="display:flex; align-items:center; gap:8px; ">
                      <div class="total-sales" style="font-size:30px; font-weight: 300; font-family:Roboto; color:#313233;">${this.currencySymbol}${salesValue}</div>
                      <div style="font-size:12px; color:#616365; font-weight=400; font-family:Roboto; line-height: 16px;">
                        across ${this.currencySymbol}${totalValue} <br />
                        total sales
                      </div>
                    </div>
                  </div>
                  `;
            }
          }
        },
        {
          data: oportunitiesSeriesData,
          type: 'bar',
          name: 'opportunity',
          barWidth: 20,
          stack: 'stack1',
          label: { show: false },
          itemStyle: { color: '#F17A22' },
          tooltip: {
            formatter: (params: any) => {
              let opportunityValue = this.formatDollarValue(params.value);
              let totalValue = this.formatDollarValue(params.data.total);
              return `<div class="tooltip-container" style="border-radius: 2px; border: 0.5px solid #F17A22; background: #ffffff; box-shadow: 0px 3px 10px 0px rgba(0, 107, 211, 0.15); padding: 8px;">
                    <div class="graph-legend"  style="font-size: 12px; font-weight: 400; font-family:Roboto; color: #313233; padding-bottom: 8px;">
                      <span class="circle" style="width: 12px; height: 12px; background-color: #F17A22; border-radius: 50%; display: inline-block; margin-right: 8px;"></span>Sales Opportunity for
                    </div>
                    <div style="display:flex; align-items:center; gap:8px; ">
                      <div class="total-sales" style="font-size:30px; font-weight: 300; font-family:Roboto; color:#313233;">${this.currencySymbol}${opportunityValue}</div>
                      <div style="font-size:12px; color:#616365; font-weight=400; font-family:Roboto; line-height: 16px;">
                        across ${this.currencySymbol}${totalValue} <br />
                        total sales
                      </div>
                    </div>
                  </div>
                  `;
            }
          }
        }]
      }
    }
  }

  private getYAxisConfiguration(data: any): any {
    return {
      type: 'category',
      axisTick: { show: false },
      inverse: true,
      data: data,
      axisLabel: {
        lineHeight: 12,
        formatter: this.getLabelFormatter(),
        ...this.getLabelTextStyle()
      }
    };
  }

  private getLabelFormatter(): (value: string) => string {
    return (value: string) => {
      value = value.split(' ')
        .map((word: string) => {
          // As some words like 'PS' and 'WW' should be in caps and not capitalized
          return (word.length > 2) ? word.charAt(0) + word.slice(1).toLowerCase() : word;
        })
        .join(' ');
      let outputString = '';
      let count = 0;
      for (let i = 0; i < value.length; i++) {
        if (value[i] === ' ' && count > 11) {
          outputString += '\n';
          count = 0;
        } else {
          outputString += value[i];
          count++;
        }
      }
      return outputString;
    };
  }

  private getLabelTextStyle(): any {
    return {
      fontWeight: '400',
      fontSize: 10,
      fontFamily: 'Roboto, "Helvetica Neue", sans-serif'
    };
  }
}
